import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useIntl, type IntlShape } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { RovoIcon } from '@atlaskit/logo';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags';
import { type TriggerProps } from '@atlaskit/popup';
import { Box, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { AgentProfileCardTrigger, ProfileClient } from '@atlaskit/profilecard';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { AgentVerifiedIcon } from '@atlaskit/rovo-agent-components/agent-verified';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { type ConversationStarter } from '@atlaskit/rovo-agent-components/ui/AgentConversationStarters';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { AgentProfileCardWithTrigger } from '@atlassian/agent-profile-card/card-with-trigger';
import { isForgeAgent, type ClientAgent } from '@atlassian/conversation-assistant-agent/types';
import { AgentMenu } from '@atlassian/conversation-assistant-agent/ui/AgentMenu';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import {
	isDefaultRovoChat,
	normalizeAccountIdFromIdentityAri,
} from '@atlassian/conversation-assistant-utils/default-agent';

import { DefaultRovoAvatar } from '../../../common/ui/default-rovo-avatar';
import { useGlobalConfig } from '../../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../../common/utils/ui-config/useUIConfig';
import { useAssistanceServiceParams } from '../../../common/utils/use-assistance-service-params';
import { useChatStoreActions } from '../../../controllers/chat-store/useChatStoreActions';
import { AgentIdentityAccountDisplay } from '../../agent-identity-account-display/agent-identity-account-display';
import parentMessages from '../messages';

import { VersionLozenge } from './version-lozenge';

const styles = cssMap({
	agentTitlePressable: {
		color: token('color.text'),
		backgroundColor: token('color.background.neutral.subtle'),
		paddingTop: token('space.050'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.075'),
		border: `${token('border.width')} solid transparent`,
		borderRadius: token('radius.medium'),
		overflow: 'hidden',

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	agentIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	agentTitleSelected: {
		backgroundColor: token('color.background.selected'),
		color: token('color.link'),
		borderColor: token('color.border.accent.blue'),

		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
			color: token('color.link'),
		},
	},
	agentTitleWrapper: {
		// Required in order to allow the agent title to truncate correctly
		minWidth: '0px',
	},
	agentTitleInline: {
		font: token('font.heading.xsmall'),
		margin: 0,
		textAlign: 'left',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	agentTitleInlineSelected: {
		color: token('color.text.selected'),
	},
});

interface AgentTitleProps {
	agent?: ClientAgent; // no agent means it we are using default agent/undefined
	defaultAgentName?: string; // pass in agent name if we are using default agent/undefined
	isDropdownOpen: boolean;
	onPress: () => void;
	triggerProps: TriggerProps;
	formatMessage: IntlShape['formatMessage'];
	cloudId: string;
	agentProfileCardProps?: {
		profileCardRef: React.RefObject<ProfileCardHandle>;
		agentId: string;
		identityAccountId: string;
		resourceClient: ProfileClient;
		onConversationStartersClick: (starter: ConversationStarter) => void;
		cloudId: string;
		viewingUserId: string | undefined;
	};
}

const AgentTitle = ({
	agent,
	defaultAgentName,
	isDropdownOpen,
	onPress,
	triggerProps,
	formatMessage,
	cloudId,
	agentProfileCardProps,
}: AgentTitleProps) => {
	const agentName = agent?.name ?? defaultAgentName;
	const identityAccountId = normalizeAccountIdFromIdentityAri(agent?.identity_account_id);
	const identityDisplayKey = identityAccountId ?? agent?.id;
	const [resolvedIdentityName, setResolvedIdentityName] = useState<{
		identityKey: string;
		name: string;
	}>();
	const mentionAgentsExperimentEnabled = isExperimentEnabled('rovo_chat_mention_agents');
	const shouldUseRelayAgentIdentityDisplay = Boolean(agent) && mentionAgentsExperimentEnabled;
	const accessibleAgentName =
		resolvedIdentityName && resolvedIdentityName.identityKey === identityDisplayKey
			? resolvedIdentityName.name
			: agentName;
	const handleIdentityNameResolved = useCallback(
		(name: string) => {
			if (!identityDisplayKey) {
				return;
			}

			setResolvedIdentityName((currentName) =>
				currentName?.identityKey === identityDisplayKey && currentName.name === name
					? currentName
					: { identityKey: identityDisplayKey, name },
			);
		},
		[identityDisplayKey],
	);

	// Only show version lozenge when the agent is loaded to avoid showing it next to the default 'Rovo' name
	const showVersionLozenge = !!agent && fg('rovo_agent_versioning_enabled');

	const content = (
		<Inline alignBlock="center" alignInline="center" space="space.100">
			{shouldUseRelayAgentIdentityDisplay && agent ? (
				<>
					<AgentIdentityAccountDisplay
						agentAri={agent.agentAri}
						agentId={agent.id}
						cloudId={cloudId}
						identityAccountId={agent.identity_account_id}
						onNameResolved={handleIdentityNameResolved}
						size="small"
						testId="agent-title-relay-name"
						textXcss={cx(
							styles.agentTitleInline,
							isDropdownOpen && styles.agentTitleInlineSelected,
						)}
					/>
					{agent.is_verified && fg('rovo_agents_agent_verification') ? <AgentVerifiedIcon /> : null}
					{showVersionLozenge && <VersionLozenge />}
				</>
			) : (
				<>
					{agent ? (
						<AgentAvatar
							size="small"
							agentId={agent.id}
							agentNamedId={agent.external_config_reference ?? agent.named_id}
							agentIdentityAccountId={agent.identity_account_id}
							imageUrl={agent.placeholderAvatarUrl}
							showBorder={false}
							isForgeAgent={
								fg('rovo_agent_support_a2a_avatar')
									? isForgeAgentByCreatorType(agent.creator_type as AgentCreatorType)
									: isForgeAgent(agent)
							}
							forgeAgentIconUrl={agent.icon}
						/>
					) : mentionAgentsExperimentEnabled ? (
						<DefaultRovoAvatar name={agentName} size="small" />
					) : (
						<Box xcss={styles.agentIcon}>
							<RovoIcon label="" shouldUseHexLogo size="xsmall" />
						</Box>
					)}
					<Inline space="space.075" alignBlock="center" xcss={styles.agentTitleWrapper}>
						<Box
							xcss={cx(styles.agentTitleInline, isDropdownOpen && styles.agentTitleInlineSelected)}
						>
							{agentName}
						</Box>
						{agent?.is_verified && fg('rovo_agents_agent_verification') ? (
							<AgentVerifiedIcon />
						) : null}
						{showVersionLozenge && <VersionLozenge />}
					</Inline>
				</>
			)}
			<ChevronDownIcon label="" size="small" />
		</Inline>
	);

	// Offsets the pressable's padding and border
	const hoverCardOffset: [number, number] = [-7, 12];

	const handlePressableClick = useCallback(
		(event: React.MouseEvent) => {
			const target = event.target as HTMLElement;

			// Check if the click originates from the agent title pressable area
			// This prevents clicks in the agent profile card from triggering the dropdown
			const isWithinAgentPressable = target.closest('[data-testid="agent-title-pressable"]');

			if (isWithinAgentPressable) {
				onPress();
			}
		},
		[onPress],
	);

	return (
		<Pressable
			{...triggerProps}
			xcss={cx(styles.agentTitlePressable, isDropdownOpen && styles.agentTitleSelected)}
			onClick={handlePressableClick}
			testId="agent-title-pressable"
			aria-label={formatMessage(parentMessages.changeAgent, { agentName: accessibleAgentName })}
		>
			{agentProfileCardProps ? (
				fg('rovo_agent_profile_card_relay') ? (
					<AgentProfileCardWithTrigger
						key={isDropdownOpen ? 'menu-open' : 'menu-closed'} // update the key to hide the profile card on menu open
						placement="bottom-end"
						trigger={isDropdownOpen ? [] : 'hover'}
						identityAccountId={agentProfileCardProps.identityAccountId}
						cloudId={agentProfileCardProps.cloudId}
						onConversationStarterClick={(prompt) => {
							agentProfileCardProps.onConversationStartersClick({
								message: prompt,
								type: 'static',
							});
						}}
					>
						{content}
					</AgentProfileCardWithTrigger>
				) : (
					<AgentProfileCardTrigger
						ref={agentProfileCardProps.profileCardRef}
						// @ts-expect-error - placement prop exists on underlying Popup component but not exposed in AgentProfileCardTriggerProps
						placement="bottom-start"
						offset={hoverCardOffset}
						trigger="hover"
						agentId={agentProfileCardProps.agentId}
						resourceClient={agentProfileCardProps.resourceClient}
						onConversationStartersClick={(starter) => {
							agentProfileCardProps.onConversationStartersClick(starter);
						}}
						cloudId={agentProfileCardProps.cloudId}
						viewingUserId={agentProfileCardProps.viewingUserId}
					>
						{content}
					</AgentProfileCardTrigger>
				)
			) : (
				content
			)}
		</Pressable>
	);
};

interface ChatHeaderTitleComponentProps {
	titleOverride?: React.ReactNode;
	isSelectedAgentFetching: boolean | undefined;
	selectedAgent: Agent | undefined;
	isAgentDropdownOpen: boolean;
	setIsAgentDropdownOpen: (isOpen: boolean) => void;
	onBrowseAgentClick?: () => void;
}

interface ProfileCardHandle {
	hideProfilecard: () => void;
}

export const ChatHeaderTitleComponent = ({
	titleOverride,
	isSelectedAgentFetching,
	selectedAgent,
	isAgentDropdownOpen,
	setIsAgentDropdownOpen,
	onBrowseAgentClick,
}: ChatHeaderTitleComponentProps): React.ReactNode => {
	const { formatMessage } = useIntl();
	const { siteId } = useGlobalConfig();
	const { createConversationOptimistically } = useChatStoreActions();
	const assistanceServiceParams = useAssistanceServiceParams({
		// This is only used in AgentMenu component,
		// so touchpointSource is set to identify the agent popup menu location
		touchpointSource: 'chat-header-agent-popup-menu',
	});
	const { sendAnalyticsEvent } = useAnalytics();
	const { sendMessage } = useChatStoreActions();
	const {
		preferences: { storeUserMessages },
	} = useUserPreferences();
	const { aaid: userId } = useProfileController();
	const { elements } = useUIConfig();
	const profileCardRef = useRef<ProfileCardHandle>(null);

	const profileClient = useMemo(
		() =>
			new ProfileClient({
				url: '/gateway/api/directory/graphql',
				productIdentifier: 'rovo',
				cloudId: siteId,
			}),
		[siteId],
	);

	const handleAgentTitlePress = useCallback(() => {
		if (!isAgentDropdownOpen) {
			profileCardRef.current?.hideProfilecard(); // prevent the agent profile card from covering the dropdown
		}
		setIsAgentDropdownOpen(!isAgentDropdownOpen);
	}, [isAgentDropdownOpen, setIsAgentDropdownOpen]);

	const handleConversationStarterClick = useCallback(
		(agent: Agent, starter: ConversationStarter) => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'agentSelectorProfileCardStarter',
				attributes: { agentId: agent.id },
			});

			sendMessage({
				prompt: encodeAdf(starter.message),
				agent,
				// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
				// value when BE user settings are fully rolled out; BE overrides it via the capability header.
				storeUserMessages,
				messageSources: {
					interactionSource: 'profileCardConversationStarters',
					followUpSource: 'none',
				},
			});

			profileCardRef.current?.hideProfilecard();
		},
		[sendAnalyticsEvent, sendMessage, storeUserMessages],
	);

	if (titleOverride) {
		return (
			<Heading as="h2" size="xsmall">
				{titleOverride}
			</Heading>
		);
	}

	if (isSelectedAgentFetching) {
		return (
			<>
				<UFOLoadHold name="rovo-chat-agent-header" />
				<Skeleton height={34} width={150} isShimmering borderRadius={token('radius.medium')} />
			</>
		);
	}

	const isDefaultAgent =
		!selectedAgent ||
		selectedAgent.is_default ||
		isDefaultRovoChat({
			agentId: selectedAgent.id,
			agentNamedId: selectedAgent.named_id,
		});

	const renderAgentTitle = (triggerProps: TriggerProps) => (
		<AgentTitle
			agent={isDefaultAgent ? undefined : selectedAgent}
			defaultAgentName={isDefaultAgent ? formatMessage(parentMessages.defaultAgentName) : undefined}
			isDropdownOpen={isAgentDropdownOpen}
			onPress={handleAgentTitlePress}
			triggerProps={triggerProps}
			formatMessage={formatMessage}
			cloudId={siteId}
			agentProfileCardProps={
				!isDefaultAgent && selectedAgent?.identity_account_id
					? {
							profileCardRef,
							agentId: selectedAgent.id,
							identityAccountId: selectedAgent.identity_account_id,
							resourceClient: profileClient,
							onConversationStartersClick: (starter) =>
								handleConversationStarterClick(selectedAgent, starter),
							cloudId: siteId,
							viewingUserId: userId,
						}
					: undefined
			}
		/>
	);

	return (
		<AgentMenu
			selectedAgent={isDefaultAgent ? undefined : selectedAgent}
			isAgentDropdownOpen={isAgentDropdownOpen}
			setIsAgentDropdownOpen={setIsAgentDropdownOpen}
			trigger={renderAgentTitle}
			placement="bottom-start"
			onClose={() => setIsAgentDropdownOpen(false)}
			siteId={siteId}
			createConversationOptimistically={createConversationOptimistically}
			assistanceServiceParams={assistanceServiceParams}
			touchpointSource="chat-header-agent-popup-menu"
			onBrowseAgentClick={onBrowseAgentClick}
			showBrowseAgents={
				elements?.browseAgentsButton !== false && !fg('rovo_chat_hide_browse_agents_and_search')
			}
			showCreateAgent={
				elements?.createAgentButton !== false && !fg('rovo_chat_hide_browse_agents_and_search')
			}
			showSearchAgents={
				elements?.agentsMenu !== false && !fg('rovo_chat_hide_browse_agents_and_search')
			}
		/>
	);
};
